/**
 * How Cocon requests are processed, week by week — on the Cocon Requests page, both views.
 *
 * Ubi, 26 Oct 2026 (relayed by Hugues): tickets *"closed in the last 7 days per subco"* and
 * *"running stats how the tickets are processed (pending, closed, etc)"*, per company and per
 * group; Hugues: *"a pending action count per partner"*, and *"each subco has to be able to see
 * his own stats because these numbers will be used in weekly meetings between Ubi and the
 * partners"*. Drawn first as screen 12 (`mockups/12-weekly-stats-proposal.html`); the rules
 * settled there hold here:
 *
 *   · Time runs ACROSS — one column per week, oldest left, newest right — and the measures
 *     are rows. The first column stays pinned; every table opens at its newest week.
 *   · Every table stays; its chart is IN ADDITION, in a panel under it that folds away.
 *   · A week with no data is EMPTY, never dropped and never drawn as zero.
 *   · One period selector drives every table and chart, here and in "Tickets over time".
 *   · Counts at a week's END (pending, open, the trend) end at the last complete week, then
 *     "Now" — this morning's read. Counts DURING a week (closed, created) include the
 *     current week, to date.
 *
 * ⚠ Nothing is scoped here. The server answers a partner with their own labels' figures only
 * (`loadRequestHistory` → `scopeRequestHistory`). The Company chip at the top of the page
 * narrows Unifiber's view through the same function, on figures it already holds.
 *
 * ⚠ These are the MORNING's figures, computed once and stored, so Ubi and a partner read the
 * same numbers all day. The tiles above are read live and may differ by what changed since.
 */

import {
  CLOSED_STATUS,
  formatNumber,
  scopeRequestHistory,
  waitingSummary,
  type HistoryCell,
  type LabelColours,
  type RequestHistory,
  type RequestWeek,
} from "@cocon-stats/shared";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { CHART_PALETTE, GroupedBars } from "@/components/charts/GroupedBars";
import { HBars } from "@/components/charts/HBars";
import { Lines, type LineRow, type LineSeries } from "@/components/charts/Lines";
import { mondayColour } from "@/components/MondayPill";
import { Picker } from "@/components/Picker";
import { PeriodPicker, dm, periodWeeks, weekAxis, weekLabel, type AxisWeek, type Period } from "@/components/period";
import { useElementWidth } from "@/hooks/useElementWidth";
import { useOpensAtNewest } from "@/hooks/useOpensAtNewest";
import { apiFetch } from "@/lib/api-client";

// ─────────────────────────────────────────────────────────────────────────────
// Data
// ─────────────────────────────────────────────────────────────────────────────

interface HistoryResponse {
  history: RequestHistory | null;
  reconciles: boolean | null;
}

export interface HistoryState {
  data: HistoryResponse | null;
  failed: boolean;
}

/** Fetched once for the page: both "Tickets over time" and the weekly tiles read it. */
export function useRequestHistory(): HistoryState {
  const [state, setState] = useState<HistoryState>({ data: null, failed: false });
  useEffect(() => {
    let cancelled = false;
    apiFetch<HistoryResponse>("/reports/monday-cocon-requests/history")
      .then((data) => !cancelled && setState({ data, failed: false }))
      .catch(() => !cancelled && setState({ data: null, failed: true }));
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

/** Unifiber's Company chip narrows the figures through the same function the server scopes with. */
function useFocused(history: RequestHistory | null | undefined, company: string | null): RequestHistory | null {
  return useMemo(() => (history ? (company ? scopeRequestHistory(history, [company]) : history) : null), [history, company]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Small parts
// ─────────────────────────────────────────────────────────────────────────────

const DASH = "—";
const AGE_BANDS = [
  { key: "lt7", label: "under 7 days", color: "#10b981" },
  { key: "d7to30", label: "7–30 days", color: "#f59e0b" },
  { key: "d30to90", label: "30–90 days", color: "#f97316" },
  { key: "over90", label: "over 90 days", color: "#ef4444" },
] as const;

/** `26 Oct, 06:31`, in Brussels — when the morning read happened. */
function readTime(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    timeZone: "Europe/Brussels",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
function signed(v: number | null): string {
  if (v === null) return DASH;
  return v > 0 ? `+${formatNumber(v)}` : v < 0 ? `−${formatNumber(-v)}` : "0";
}
function sumOf(values: Array<number | null>): number | null {
  return values.some((v) => v !== null) ? values.reduce<number>((s, v) => s + (v ?? 0), 0) : null;
}
function Dot({ color }: { color: string }) {
  return <span className="mr-2 inline-block h-2 w-2 rounded-full align-middle" style={{ background: color }} />;
}

/** Draws its child at the width it is given — charts are drawn to their panel, never letterboxed. */
function Sized({ children }: { children: (width: number) => ReactNode }) {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  return <div ref={ref}>{width > 0 && children(width)}</div>;
}

/** Bars while there are 12 weeks or fewer, lines beyond: a year of weekly bars is two pixels wide. */
function WeeklyChart({ rows, series, height = 280 }: { rows: LineRow[]; series: LineSeries[]; height?: number }) {
  return (
    <Sized>
      {(width) =>
        rows.length <= 12 ? (
          <GroupedBars
            rows={rows.map((r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v ?? undefined])) as never)}
            series={series}
            width={width}
            height={height}
          />
        ) : (
          <Lines rows={rows} series={series} width={width} height={height} />
        )
      }
    </Sized>
  );
}

function ChartPanel({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-t border-slate-100">
      <button
        type="button"
        className="flex w-full items-center justify-between px-5 py-3 text-xs font-semibold text-slate-600"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>{title}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

function Band({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <div className="gradient-band flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-white">
      <span className="text-[11px] font-bold uppercase tracking-wider">{title}</span>
      {right && <span className="text-[11px] font-medium text-white/80">{right}</span>}
    </div>
  );
}

interface TimeRow {
  key: string;
  label: ReactNode;
  values: Array<number | null>;
  cls?: string;
  total?: boolean;
  tail?: ReactNode[];
}

/** Weeks across, measures down, the first column pinned, opening at the newest week. */
function TimeTable({
  corner,
  weeks,
  rows,
  tailHeads = [],
}: {
  corner: string;
  weeks: Array<AxisWeek<RequestWeek>>;
  rows: TimeRow[];
  tailHeads?: ReactNode[];
}) {
  const box = useOpensAtNewest<HTMLDivElement>([weeks.length, weeks[weeks.length - 1]?.key, rows.length]);

  return (
    <div ref={box} className="custom-scrollbar overflow-x-auto">
      <table className="tbl tbl-tight tbl-time">
        <thead>
          <tr>
            <th>{corner}</th>
            {weeks.map((w) => (
              <th key={w.key} className="text-right" title={`${weekLabel(w.key)}: ${dm(w.start)} – ${dm(w.end)}`}>
                {weekLabel(w.key)}
                <br />
                <span className="text-[10px] font-normal normal-case tracking-normal text-slate-400">
                  {dm(w.start)} – {dm(w.end)}
                </span>
                {!w.data ? (
                  <>
                    <br />
                    <span className="pill pill-slate" title="The board's history does not reach this week">
                      no data
                    </span>
                  </>
                ) : w.data.toDate ? (
                  <>
                    <br />
                    <span className="pill pill-blue" title="This week is still running: to this morning's read">
                      to date
                    </span>
                  </>
                ) : null}
              </th>
            ))}
            {tailHeads}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={r.total ? "total" : undefined}>
              <td className="whitespace-nowrap font-semibold text-slate-700">{r.label}</td>
              {r.values.map((v, i) => (
                <td key={weeks[i]!.key} className={`num text-right ${v === null || v === 0 ? "text-slate-300" : (r.cls ?? "text-slate-700")}`}>
                  {v === null ? DASH : formatNumber(v)}
                </td>
              ))}
              {r.tail}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const nowHead = (asOf: string) => (
  <th key="now" className="tail text-right" title={`This morning's read, ${readTime(asOf)}`}>
    Now
    <br />
    <span className="text-[10px] font-normal normal-case tracking-normal text-slate-400">{readTime(asOf)}</span>
  </th>
);
const changeHead = (
  <th key="change" className="tail text-right" title="Now against the first week end of the period">
    Change
  </th>
);
function nowAndChange(first: number | null, now: number, bold = true): ReactNode[] {
  const change = first === null ? null : now - first;
  return [
    <td key="now" className={`num tail text-right ${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>
      {formatNumber(now)}
    </td>,
    <td key="change" className={`num tail text-right ${change === null || change === 0 ? "text-slate-400" : change > 0 ? "text-red-600" : "text-emerald-700"}`}>
      {signed(change)}
    </td>,
  ];
}

/** The weeks whose END the counts describe: complete weeks only — the running one is "Now". */
function endedWeeks(shown: Array<AxisWeek<RequestWeek>>): Array<AxisWeek<RequestWeek>> {
  return shown.filter((w) => !w.data?.toDate);
}
function chartLabel(w: AxisWeek<RequestWeek>): { label: string; title: string } {
  return { label: weekLabel(w.key), title: `${weekLabel(w.key)} (${dm(w.start)} – ${dm(w.end)})` };
}

function Unavailable({ state, what }: { state: HistoryState; what: string }) {
  if (state.failed) return <p className="p-5 text-sm text-slate-500">{what} could not be loaded.</p>;
  if (!state.data) return <p className="p-5 text-sm text-slate-400">Loading {what.toLowerCase()}…</p>;
  // ⚠ Never empty weeks drawn as if nothing had happened: the history is built by the morning run.
  return (
    <p className="p-5 text-sm text-slate-500">
      {what} has not been computed yet — the morning run builds it from the board&rsquo;s history.
    </p>
  );
}

function NotReconciled() {
  return (
    <div className="flex items-start gap-2.5 px-5 py-3" style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a" }}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#b45309" }} />
      <p className="text-xs" style={{ color: "#b45309" }}>
        This morning&rsquo;s rebuild of the board&rsquo;s history does not fully reconcile with the board — some weekly
        figures below may be off. Unifiber has been told by the morning job&rsquo;s log.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// T. Tickets over time
// ─────────────────────────────────────────────────────────────────────────────

type Split = "status" | "company" | "group";
const SPLIT_INDEX: Record<Split, 0 | 1 | 2> = { status: 0, company: 1, group: 2 };
const SPLIT_NAME: Record<Split, string> = { status: "Status", company: "Partner", group: "Group" };

/**
 * The number of tickets on the board at every week end, split by status, partner or group,
 * filtered by each. Folded until opened. Done is unticked by default: it only accumulates
 * and would flatten every other line.
 */
export function TicketsOverTime({
  state,
  company,
  colours,
  period,
  onPeriod,
}: {
  state: HistoryState;
  company: string | null;
  colours: LabelColours;
  period: Period;
  onPeriod: (p: Period) => void;
}) {
  const [open, setOpen] = useState(false);
  const [split, setSplit] = useState<Split>("status");
  const h = useFocused(state.data?.history, company);
  const defaultStatuses = useMemo(() => (h ? h.statuses.filter((s) => s !== CLOSED_STATUS) : []), [h]);
  const [statuses, setStatuses] = useState<string[] | null>(null); // null = the default
  const [partners, setPartners] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);

  const axis = useMemo(() => weekAxis(h?.weeks ?? []), [h]);
  const shown = useMemo(() => periodWeeks(axis, period), [axis, period]);
  const weeks = endedWeeks(shown);

  const chosenStatuses = statuses ?? defaultStatuses;
  const pass = (c: HistoryCell) =>
    (chosenStatuses.length === 0 || chosenStatuses.includes(c[0])) &&
    (partners.length === 0 || partners.includes(c[1])) &&
    (groups.length === 0 || groups.includes(c[2]));
  const tally = (cells: HistoryCell[]): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const c of cells) if (pass(c)) out[c[SPLIT_INDEX[split]]] = (out[c[SPLIT_INDEX[split]]] ?? 0) + c[3];
    return out;
  };
  const colourOf = (v: string) => mondayColour(colours, split, v);

  const band = h ? `Split by ${SPLIT_NAME[split].toLowerCase()} · ${weeks.length} week ends and now` : undefined;

  return (
    <section className="card overflow-hidden p-0">
      <Band title="Tickets Over Time" right={band} />
      <button
        type="button"
        className="flex w-full items-center justify-between border-b border-slate-100 px-5 py-3 text-xs font-semibold text-slate-600"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>Historical trend — the number of tickets at each week end</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open &&
        (!h ? (
          <Unavailable state={state} what="The history" />
        ) : (
          (() => {
            const perWeek = weeks.map((w) => (w.data ? tally(w.data.cells) : null));
            const now = tally(h.now);
            const seen = new Set<string>(Object.keys(now));
            for (const t of perWeek) for (const k of Object.keys(t ?? {})) seen.add(k);
            const values = [...seen].sort((a, b) => (now[b] ?? 0) - (now[a] ?? 0) || a.localeCompare(b));
            const drawn = values.slice(0, 8);
            const rows: TimeRow[] = values.map((v) => {
              const vals = perWeek.map((t) => (t ? (t[v] ?? 0) : null));
              return {
                key: v,
                label: (
                  <>
                    <Dot color={colourOf(v)} />
                    {v}
                  </>
                ),
                cls: "text-slate-600",
                values: vals,
                tail: nowAndChange(vals.find((x) => x !== null) ?? null, now[v] ?? 0),
              };
            });
            const totals = perWeek.map((t) => (t ? Object.values(t).reduce((s, n) => s + n, 0) : null));
            rows.push({
              key: "__total",
              label: "Total",
              cls: "font-bold text-slate-800",
              total: true,
              values: totals,
              tail: nowAndChange(totals.find((x) => x !== null) ?? null, Object.values(now).reduce((s, n) => s + n, 0)),
            });

            return (
              <>
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
                  <PeriodPicker axis={axis} shown={shown} period={period} onChange={onPeriod} />
                </div>
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
                  <span className="eyebrow mr-1">Split by</span>
                  {(["status", "company", "group"] as const).map((s) => (
                    <button key={s} type="button" className={`chip${split === s ? " chip-active" : ""}`} onClick={() => setSplit(s)}>
                      {SPLIT_NAME[s]}
                    </button>
                  ))}
                  <span className="w-4" />
                  <span className="eyebrow mr-1">Filter</span>
                  <Picker label="Status" options={h.statuses} selected={chosenStatuses} onChange={setStatuses} />
                  {h.companies.length > 1 && <Picker label="Partner" options={h.companies} selected={partners} onChange={setPartners} />}
                  <Picker label="Group" options={h.groups} selected={groups} onChange={setGroups} />
                  <button
                    type="button"
                    className="ml-auto text-xs font-semibold"
                    style={{ color: "#ed125f" }}
                    onClick={() => {
                      setSplit("status");
                      setStatuses(null);
                      setPartners([]);
                      setGroups([]);
                    }}
                  >
                    Reset
                  </button>
                </div>
                <div className="p-5">
                  <Sized>
                    {(width) => (
                      <Lines
                        width={width}
                        height={320}
                        series={drawn.map((v) => ({ key: v, label: v, color: colourOf(v) }))}
                        rows={weeks.map((w, i) => {
                          const r: LineRow = chartLabel(w);
                          for (const v of drawn) r[v] = perWeek[i] ? (perWeek[i]![v] ?? 0) : null;
                          return r;
                        })}
                      />
                    )}
                  </Sized>
                  <p className="mt-2 text-xs text-slate-400">
                    Tickets on the board at each week end (Monday 00:00), each with the status, partner and group it had
                    then; archived tickets left out. {values.length > 8 ? `The 8 largest are drawn; all ${values.length} are in the table. ` : ""}
                    {!chosenStatuses.includes(CLOSED_STATUS) && chosenStatuses.length > 0 ? "Done is unticked by default: it only accumulates." : ""}
                  </p>
                </div>
                <div className="px-5 pb-5">
                  <TimeTable corner={SPLIT_NAME[split]} weeks={weeks} rows={rows} tailHeads={[nowHead(h.asOf), changeHead]} />
                </div>
              </>
            );
          })()
        ))}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A, B, C — pending, closed, how tickets move
// ─────────────────────────────────────────────────────────────────────────────

export function RequestWeekly({
  state,
  company,
  colours,
  period,
  onPeriod,
}: {
  state: HistoryState;
  company: string | null;
  colours: LabelColours;
  period: Period;
  onPeriod: (p: Period) => void;
}) {
  const h = useFocused(state.data?.history, company);
  const axis = useMemo(() => weekAxis(h?.weeks ?? []), [h]);
  const shown = useMemo(() => periodWeeks(axis, period), [axis, period]);

  if (!h) {
    return (
      <section className="card overflow-hidden p-0">
        <Band title="Week By Week" />
        <Unavailable state={state} what="The weekly figures" />
      </section>
    );
  }

  const partners = h.companies;
  const partnerColour = (p: string) => mondayColour(colours, "company", p);
  const label = company ?? (partners.length === 1 ? partners[0]! : "All partners");

  return (
    <div className="space-y-6">
      <section className="card space-y-2 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <PeriodPicker axis={axis} shown={shown} period={period} onChange={onPeriod} />
        </div>
        <p className="text-xs text-slate-500">
          Week by week since {weekLabel(h.weeks[0]!.week)}, rebuilt from the board&rsquo;s own history and computed once
          this morning ({readTime(h.asOf)}), so everybody reads the same figures all day — the tiles above are read live.
          Weeks run Monday to Sunday. Archived tickets are left out of every week; a ticket counts for the partner, group
          and status it had at the time.{" "}
          {company ? `Showing ${company}, as chosen at the top of the page; ` : ""}
          the group, POP and search filters apply to the tiles above only.
        </p>
      </section>

      {state.data?.reconciles === false && (
        <section className="card overflow-hidden p-0">
          <NotReconciled />
        </section>
      )}

      <PendingByPartner h={h} shown={shown} partnerColour={partnerColour} />
      <ClosedTickets h={h} shown={shown} partnerColour={partnerColour} colours={colours} label={label} />
      <HowTicketsMove h={h} shown={shown} colours={colours} label={label} />
    </div>
  );
}

function PendingByPartner({
  h,
  shown,
  partnerColour,
}: {
  h: RequestHistory;
  shown: Array<AxisWeek<RequestWeek>>;
  partnerColour: (p: string) => string;
}) {
  const weeks = endedWeeks(shown);
  const pending = (w: AxisWeek<RequestWeek>, p: string) => (w.data ? (w.data.byPartner[p]?.pendingEnd ?? 0) : null);

  const lines = h.companies
    .map((p) => {
      const vals = weeks.map((w) => pending(w, p));
      return { p, vals, now: h.waiting[p]?.length ?? 0, ages: waitingSummary(h.waiting[p] ?? []) };
    })
    .filter((r) => r.now > 0 || r.vals.some((v) => v))
    .sort((a, b) => b.now - a.now || a.p.localeCompare(b.p));

  const ageCells = (ages: ReturnType<typeof waitingSummary>, bold = false): ReactNode[] => [
    <td key="ages" className="tail" style={{ minWidth: 200 }}>
      {ages.n === 0 ? (
        <span className="text-slate-300">{DASH}</span>
      ) : (
        <div className="flex items-center gap-2" title={AGE_BANDS.map((b) => `${ages[b.key]} waiting ${b.label}`).join(" · ")}>
          <div className="flex h-2 min-w-16 flex-1 overflow-hidden rounded-full bg-slate-100">
            {AGE_BANDS.map((b) => (
              <span key={b.key} style={{ width: `${(ages[b.key] / ages.n) * 100}%`, background: b.color }} />
            ))}
          </div>
          <span className="num whitespace-nowrap text-[11px] text-slate-500">{AGE_BANDS.map((b) => ages[b.key]).join(" · ")}</span>
        </div>
      )}
    </td>,
    <td key="median" className={`num tail text-right ${bold ? "font-bold text-slate-800" : "text-slate-600"}`}>
      {ages.median === null ? DASH : `${ages.median} d`}
    </td>,
  ];

  const rows: TimeRow[] = lines.map((r) => ({
    key: r.p,
    label: (
      <>
        <Dot color={partnerColour(r.p)} />
        {r.p}
      </>
    ),
    cls: "text-slate-600",
    values: r.vals,
    tail: [...nowAndChange(r.vals.find((v) => v !== null) ?? null, r.now), ...ageCells(r.ages)],
  }));
  if (lines.length > 1) {
    const totals = weeks.map((_, i) => sumOf(lines.map((r) => r.vals[i] ?? null)));
    const all = waitingSummary(lines.flatMap((r) => h.waiting[r.p] ?? []));
    rows.push({
      key: "__total",
      label: "Total",
      cls: "font-bold text-slate-800",
      total: true,
      values: totals,
      tail: [...nowAndChange(totals.find((v) => v !== null) ?? null, all.n), ...ageCells(all, true)],
    });
  }
  const drawn = lines.slice(0, 6);

  return (
    <section className="card overflow-hidden p-0">
      <Band title="Pending Action By Partner" right={`At each week end, and now (${readTime(h.asOf)})`} />
      <div className="p-5">
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500">No ticket has been pending action by a partner in this period.</p>
        ) : (
          <TimeTable
            corner="Partner"
            weeks={weeks}
            rows={rows}
            tailHeads={[
              nowHead(h.asOf),
              changeHead,
              <th key="ages" className="tail" title="Waiting under 7 days · 7–30 · 30–90 · over 90 days">
                Waiting now
              </th>,
              <th key="median" className="tail text-right" title="Half the tickets have waited longer than this">
                Median
              </th>,
            ]}
          />
        )}
        <p className="mt-3 text-xs text-slate-400">
          Tickets in &ldquo;Pending action by Partner&rdquo;. Each week column is the count at that week&rsquo;s end
          (Monday 00:00). Waiting now: under 7 days (green) · 7–30 (amber) · 30–90 (orange) · over 90 (red), in calendar
          days since the ticket last entered the status, or since it was created in it.
        </p>
      </div>
      {drawn.length > 0 && (
        <ChartPanel title="Chart">
          <p className="eyebrow mb-2">Pending action by Partner at each week end</p>
          <Sized>
            {(width) => (
              <Lines
                width={width}
                height={320}
                series={drawn.map((r) => ({ key: r.p, label: r.p, color: partnerColour(r.p) }))}
                rows={weeks.map((w) => {
                  const row: LineRow = chartLabel(w);
                  for (const r of drawn) row[r.p] = pending(w, r.p);
                  return row;
                })}
              />
            )}
          </Sized>
        </ChartPanel>
      )}
    </section>
  );
}

function ClosedTickets({
  h,
  shown,
  partnerColour,
  colours,
  label,
}: {
  h: RequestHistory;
  shown: Array<AxisWeek<RequestWeek>>;
  partnerColour: (p: string) => string;
  colours: LabelColours;
  label: string;
}) {
  const last7 = h.closedLast7;
  const byPartner: Record<string, number> = {};
  const byGroup: Record<string, number> = {};
  for (const [p, g, n] of last7.cells) {
    byPartner[p] = (byPartner[p] ?? 0) + n;
    byGroup[g] = (byGroup[g] ?? 0) + n;
  }
  const total7 = Object.values(byPartner).reduce((s, n) => s + n, 0);
  // Every partner with work on the board, closes or not: "0 closed" is the figure that matters.
  const partners7 = h.companies.filter((p) => byPartner[p] || h.waiting[p]?.length || h.now.some((c) => c[1] === p && c[0] !== CLOSED_STATUS));

  const closed = (w: AxisWeek<RequestWeek>, p: string) => (w.data ? (w.data.byPartner[p]?.closed ?? 0) : null);
  const inPeriod = (p: string) => shown.reduce((s, w) => s + (closed(w, p) ?? 0), 0);
  const cols = h.companies
    .filter((p) => inPeriod(p) > 0 || partners7.includes(p))
    .sort((a, b) => inPeriod(b) - inPeriod(a) || a.localeCompare(b));
  const totals = shown.map((w) => (w.data ? h.companies.reduce((s, p) => s + (closed(w, p) ?? 0), 0) : null));
  const single = h.companies.length === 1;
  const rows: TimeRow[] = single
    ? [{ key: "__total", label, cls: "font-bold text-emerald-700", total: true, values: totals }]
    : [
        ...cols.map((p) => ({
          key: p,
          label: (
            <>
              <Dot color={partnerColour(p)} />
              {p}
            </>
          ),
          values: shown.map((w) => closed(w, p)),
        })),
        { key: "__total", label: "Total", cls: "font-bold text-emerald-700", total: true, values: totals },
      ];

  return (
    <section className="card overflow-hidden p-0">
      <Band title="Closed Tickets" right={`Last 7 days: ${readTime(last7.from)} → ${readTime(last7.to)}`} />
      <div className="grid gap-6 border-b border-slate-100 p-5 lg:grid-cols-2">
        <div>
          <p className="eyebrow mb-2">
            Last 7 days{single ? "" : ", per partner"} ·{" "}
            <span className="num text-slate-700">{formatNumber(total7)}</span> closed
          </p>
          {partners7.length === 0 ? (
            <p className="text-sm text-slate-400">{DASH}</p>
          ) : (
            <HBars
              rows={partners7
                .map((p) => ({ label: p, n: byPartner[p] ?? 0, color: partnerColour(p) }))
                .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label))}
            />
          )}
        </div>
        <div>
          <p className="eyebrow mb-2">Last 7 days, per group</p>
          {Object.keys(byGroup).length === 0 ? (
            <p className="text-sm text-slate-400">No ticket closed in the last 7 days.</p>
          ) : (
            <HBars
              rows={Object.entries(byGroup)
                .map(([g, n]) => ({ label: g, n, color: mondayColour(colours, "group", g) }))
                .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label))}
            />
          )}
        </div>
      </div>
      <div className="p-5">
        <p className="eyebrow mb-2">Closed per week</p>
        <TimeTable corner="Closed" weeks={shown} rows={rows} />
        <p className="mt-3 text-xs text-slate-400">
          A ticket counts as closed when its status moves into Done, in the week it happens, for the partner and group it
          had then. A ticket closed, reopened and closed again counts each time. The last 7 days are rolling, to this
          morning&rsquo;s read.
        </p>
      </div>
      <ChartPanel title="Chart">
        <p className="eyebrow mb-2">Closed per week</p>
        <WeeklyChart
          series={[{ key: "closed", label, color: single ? partnerColour(h.companies[0]!) : CHART_PALETTE.emerald! }]}
          rows={shown.map((w, i) => ({ ...chartLabel(w), closed: totals[i] ?? null }))}
        />
      </ChartPanel>
    </section>
  );
}

function HowTicketsMove({
  h,
  shown,
  colours,
  label,
}: {
  h: RequestHistory;
  shown: Array<AxisWeek<RequestWeek>>;
  colours: LabelColours;
  label: string;
}) {
  type Key = "openStart" | "createdOpen" | "reopened" | "closed" | "relabelIn" | "relabelOut" | "openEnd";
  const part = (w: AxisWeek<RequestWeek>, k: Key) =>
    w.data ? Object.values(w.data.byPartner).reduce((s, f) => s + f[k], 0) : null;
  const relabel = (w: AxisWeek<RequestWeek>) => (w.data ? part(w, "relabelIn")! - part(w, "relabelOut")! : null);
  const series = (k: Key) => shown.map((w) => part(w, k));
  const anyRelabel = shown.some((w) => (relabel(w) ?? 0) !== 0);
  const broken = shown.filter(
    (w) =>
      w.data &&
      part(w, "openStart")! + part(w, "createdOpen")! + part(w, "reopened")! - part(w, "closed")! + relabel(w)! !== part(w, "openEnd"),
  ).length;

  const rows: TimeRow[] = [
    { key: "start", label: "Open at start", cls: "text-slate-500", values: series("openStart") },
    { key: "created", label: "+ Created", values: series("createdOpen") },
    { key: "reopened", label: "+ Reopened", cls: "text-slate-500", values: series("reopened") },
    { key: "closed", label: "− Closed", cls: "font-bold text-emerald-700", values: series("closed") },
    ...(anyRelabel ? [{ key: "relabel", label: "± Relabelled between partners", cls: "text-slate-500", values: shown.map(relabel) }] : []),
    { key: "end", label: "= Open at end", cls: "font-bold text-slate-900", total: true, values: series("openEnd") },
  ];

  const weeks = endedWeeks(shown);
  const openStatuses = h.statuses.filter((s) => s !== CLOSED_STATUS);

  return (
    <section className="card overflow-hidden p-0">
      <Band title="How Tickets Move" right={label} />
      <div className="p-5">
        <TimeTable corner="Tickets" weeks={shown} rows={rows} />
        <p className="mt-3 text-xs" style={broken ? { color: "#b45309" } : undefined}>
          <span className={broken ? "" : "text-slate-400"}>
            {broken
              ? `⚠ ${broken} week${broken === 1 ? "" : "s"} shown do${broken === 1 ? "es" : ""} not add up.`
              : `Every week shown adds up: open at start + created + reopened − closed${anyRelabel ? " ± relabelled" : ""} = open at end.`}{" "}
            A ticket created directly as Done was never open and is not counted as created. A relabel moves an open
            ticket from one partner to another; across all partners it nets to zero.
          </span>
        </p>
      </div>
      <ChartPanel title="Charts">
        <div className="grid gap-8 xl:grid-cols-2">
          <div className="min-w-0">
            <p className="eyebrow mb-2">Open tickets at week end, by status</p>
            <Sized>
              {(width) => (
                <Lines
                  width={width}
                  height={280}
                  series={openStatuses.map((s) => ({ key: s, label: s, color: mondayColour(colours, "status", s) }))}
                  rows={weeks.map((w) => {
                    const r: LineRow = chartLabel(w);
                    for (const s of openStatuses) r[s] = w.data ? w.data.cells.filter((c) => c[0] === s).reduce((n, c) => n + c[3], 0) : null;
                    return r;
                  })}
                />
              )}
            </Sized>
          </div>
          <div className="min-w-0">
            <p className="eyebrow mb-2">Created and closed per week</p>
            <WeeklyChart
              series={[
                { key: "created", label: "Created", color: CHART_PALETTE.slate! },
                { key: "closed", label: "Closed", color: CHART_PALETTE.emerald! },
              ]}
              rows={shown.map((w) => ({ ...chartLabel(w), created: part(w, "createdOpen"), closed: part(w, "closed") }))}
            />
          </div>
        </div>
      </ChartPanel>
    </section>
  );
}
